import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCurrentLang, localizePath } from "@/i18n/language";

type BlockKey =
  | "villa"
  | "ausfluege"
  | "touren"
  | "massage_dinner"
  | "transfers"
  | "vornach";

const BLOCKS: { key: BlockKey; titleKey: string; descKey: string; icon: string }[] = [
  {
    key: "villa",
    titleKey: "baustein_block_villa_title",
    descKey: "baustein_block_villa_desc",
    icon: "ri-home-5-line",
  },
  {
    key: "ausfluege",
    titleKey: "baustein_block_ausfluege_title",
    descKey: "baustein_block_ausfluege_desc",
    icon: "ri-map-pin-line",
  },
  {
    key: "touren",
    titleKey: "baustein_block_touren_title",
    descKey: "baustein_block_touren_desc",
    icon: "ri-compass-3-line",
  },
  {
    key: "massage_dinner",
    titleKey: "baustein_block_massage_dinner_title",
    descKey: "baustein_block_massage_dinner_desc",
    icon: "ri-hand-heart-line",
  },
  {
    key: "transfers",
    titleKey: "baustein_block_transfers_title",
    descKey: "baustein_block_transfers_desc",
    icon: "ri-car-line",
  },
  {
    key: "vornach",
    titleKey: "baustein_block_vornach_title",
    descKey: "baustein_block_vornach_desc",
    icon: "ri-calendar-line",
  },
];

export default function BausteineSelection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = useCurrentLang();

  const [selected, setSelected] = useState<BlockKey[]>([]);
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: BlockKey) => {
    setError(null);
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleContinue = () => {
    if (selected.length === 0) {
      setError(t("baustein_select_blocks_error"));
      return;
    }
    const query = `?blocks=${selected.join(",")}`;
    navigate(`${localizePath("/plan-your-stay", lang)}${query}`);
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg pt-3 md:pt-4 px-6 md:px-10 pb-6 md:pb-10">
      <div className="text-center max-w-2xl mx-auto mb-7 md:mb-8">
        <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-3">
          {t("baustein_select_hint")}
        </h3>
        <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
          {t("baustein_booking_sub")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {BLOCKS.map((block) => {
          const active = selected.includes(block.key);
          return (
            <button
              key={block.key}
              type="button"
              onClick={() => toggle(block.key)}
              className={`flex items-start gap-4 text-left rounded-lg border p-5 transition-colors cursor-pointer ${
                active
                  ? "border-primary-500 bg-primary-50"
                  : "border-background-200 bg-background-50 hover:border-primary-300"
              }`}
            >
              <div
                className={`w-11 h-11 shrink-0 flex items-center justify-center rounded-md ${
                  active ? "bg-primary-600 text-background-50" : "bg-accent-100 text-accent-700"
                }`}
              >
                <i className={`${block.icon} text-xl`}></i>
              </div>
              <span className="flex-1">
                <span className="block font-heading text-base text-foreground-950 font-medium leading-snug">
                  {t(block.titleKey)}
                </span>
                <span className="block text-sm text-foreground-600 mt-1 leading-relaxed">
                  {t(block.descKey)}
                </span>
              </span>
              <i
                className={`text-xl mt-0.5 ${
                  active
                    ? "ri-checkbox-circle-fill text-primary-600"
                    : "ri-checkbox-blank-circle-line text-foreground-300"
                }`}
              ></i>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="flex items-center gap-2 text-sm text-secondary-700 mt-6">
          <i className="ri-error-warning-line text-accent-600"></i>
          {error}
        </p>
      )}

      <div className="mt-8 flex items-center justify-between gap-4">
        <span className="text-sm text-foreground-600">
          {selected.length} {lang === "de" ? "ausgewählt" : "selected"}
        </span>
        <button
          type="button"
          onClick={handleContinue}
          className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-background-50 px-8 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
        >
          {t("baustein_next")}
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
    </div>
  );
}